import React, { FormEvent, useEffect, useRef, useState } from 'react';

import hasuraLogo from '../images/hasura_black_logo.svg';
import {
  PhoneOnboardingApi,
  phoneOnboardingApi,
  PhoneOnboardingError,
} from './phoneOnboardingApi';

const DEFAULT_COOLDOWN_SECONDS = 60;
const E164_PATTERN = /^\+[1-9]\d{7,14}$/;
const OTP_PATTERN = /^\d{6}$/;

const errorMessages: Record<string, string> = {
  INVALID_PHONE_NUMBER:
    'Enter a valid phone number, including the country code.',
  PHONE_NUMBER_ALREADY_IN_USE:
    'This phone number cannot be used. Try a different number.',
  PHONE_ALREADY_VERIFIED: 'This account already has a verified phone number.',
  RATE_LIMITED: 'Too many attempts. Please wait before trying again.',
  PHONE_PROVIDER_UNAVAILABLE:
    'Phone verification is temporarily unavailable. Please try again later.',
  ONBOARDING_SESSION_EXPIRED:
    'Your verification session has expired. Sign in again to continue.',
  PHONE_VERIFICATION_DISABLED: 'Phone verification is not available.',
  INTERNAL_ERROR: 'Something went wrong. Please try again.',
};

const normalizePhoneInput = (value: string) =>
  value.trim().replace(/[\s().-]/g, '');

const getErrorMessage = (error: unknown) => {
  if (error instanceof PhoneOnboardingError) {
    return errorMessages[error.code] || errorMessages.INTERNAL_ERROR;
  }
  return errorMessages.INTERNAL_ERROR;
};

const isExpiredError = (error: unknown) =>
  error instanceof PhoneOnboardingError &&
  error.code === 'ONBOARDING_SESSION_EXPIRED';

type Props = {
  api?: PhoneOnboardingApi;
  cooldownSeconds?: number;
  expiresAt?: string | null;
  onVerified: () => void;
  onSessionExpired: () => void;
  onAbandon: () => void;
};

export const PhoneOnboardingPage: React.FC<Props> = ({
  api = phoneOnboardingApi,
  cooldownSeconds = DEFAULT_COOLDOWN_SECONDS,
  expiresAt,
  onVerified,
  onSessionExpired,
  onAbandon,
}) => {
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(
    null
  );
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!expiresAt) return;

    const expiresAtTimestamp = new Date(expiresAt).getTime();
    let timeout: number | undefined;

    const scheduleExpiration = () => {
      const remainingMilliseconds = expiresAtTimestamp - Date.now();
      if (!Number.isFinite(expiresAtTimestamp) || remainingMilliseconds <= 0) {
        onSessionExpired();
        return;
      }

      timeout = window.setTimeout(
        scheduleExpiration,
        Math.min(remainingMilliseconds, 2_147_483_647)
      );
    };

    scheduleExpiration();
    return () => {
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, [expiresAt, onSessionExpired]);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const interval = window.setInterval(() => {
      setCooldownRemaining(current => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [cooldownRemaining]);

  useEffect(() => {
    if (step === 'code') {
      codeInputRef.current?.focus();
    }
  }, [step]);

  const handleError = (requestError: unknown) => {
    setError(getErrorMessage(requestError));
    if (isExpiredError(requestError)) {
      onSessionExpired();
    }
  };

  const sendCode = async (number: string) => {
    if (isSubmitting) return;

    const normalized = normalizePhoneInput(number);
    if (!E164_PATTERN.test(normalized)) {
      setError(errorMessages.INVALID_PHONE_NUMBER);
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      const result = await api.sendCode(normalized);
      setPhoneNumber(result.phoneNumber);
      setPhoneInput(result.phoneNumber);
      setCode('');
      setAttemptsRemaining(null);
      setCooldownRemaining(cooldownSeconds);
      setStep('code');
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneSubmit = (event: FormEvent) => {
    event.preventDefault();
    void sendCode(phoneInput);
  };

  const handleVerifySubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (!OTP_PATTERN.test(code)) {
      setError('Enter the six-digit verification code.');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      const result = await api.verifyCode(code);
      if (result.status === 'incorrect') {
        setAttemptsRemaining(result.attemptsRemaining);
        setError(
          `That code is incorrect. ${result.attemptsRemaining} ${
            result.attemptsRemaining === 1 ? 'attempt' : 'attempts'
          } remaining.`
        );
        setCode('');
        return;
      }

      onVerified();
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = () => {
    if (cooldownRemaining > 0 || isSubmitting) return;
    void sendCode(phoneNumber);
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <section
        aria-labelledby="phone-onboarding-title"
        className="w-full max-w-md rounded-lg bg-white px-8 py-10 shadow-lg"
      >
        <img className="mb-8 h-8" src={hasuraLogo} alt="Hasura" />
        {step === 'phone' ? (
          <>
            <h1
              id="phone-onboarding-title"
              className="text-2xl font-semibold text-slate-900"
            >
              Verify your phone number
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              We use a one-time SMS code to help protect Hasura Cloud from
              abuse.
            </p>
            <form className="mt-8" onSubmit={handlePhoneSubmit}>
              <label
                className="block text-sm font-medium text-slate-800"
                htmlFor="phone-number"
              >
                Phone number
              </label>
              <input
                autoComplete="tel"
                autoFocus
                className="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                id="phone-number"
                inputMode="tel"
                onChange={event => setPhoneInput(event.target.value)}
                placeholder="+1 415 555 2671"
                type="tel"
                value={phoneInput}
              />
              <p className="mt-2 text-xs text-slate-500">
                Include the country code, for example +1.
              </p>
              {error && (
                <div
                  aria-live="polite"
                  className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
                  role="alert"
                >
                  {error}
                </div>
              )}
              <button
                className="text-slate-950 mt-6 w-full rounded-md bg-yellow-400 px-4 py-2.5 text-sm font-semibold hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting ? 'Sending code…' : 'Send verification code'}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1
              id="phone-onboarding-title"
              className="text-2xl font-semibold text-slate-900"
            >
              Enter your verification code
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              We sent a six-digit code to{' '}
              <strong className="font-medium text-slate-900">
                {phoneNumber}
              </strong>
              .
            </p>
            <form className="mt-8" onSubmit={handleVerifySubmit}>
              <label
                className="block text-sm font-medium text-slate-800"
                htmlFor="verification-code"
              >
                Verification code
              </label>
              <input
                ref={codeInputRef}
                autoComplete="one-time-code"
                className="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2 text-center font-mono text-xl tracking-[0.35em] text-slate-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                id="verification-code"
                inputMode="numeric"
                maxLength={6}
                onChange={event =>
                  setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
                pattern="[0-9]{6}"
                type="text"
                value={code}
              />
              {error && (
                <div
                  aria-live="polite"
                  className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
                  role="alert"
                >
                  {error}
                </div>
              )}
              {!error && attemptsRemaining !== null && (
                <p className="mt-3 text-sm text-slate-600">
                  {attemptsRemaining} attempts remaining.
                </p>
              )}
              <button
                className="text-slate-950 mt-6 w-full rounded-md bg-yellow-400 px-4 py-2.5 text-sm font-semibold hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting ? 'Verifying…' : 'Verify and continue'}
              </button>
              <button
                className="mt-4 w-full text-sm font-medium text-blue-700 hover:text-blue-600 disabled:cursor-not-allowed disabled:text-slate-400"
                disabled={cooldownRemaining > 0 || isSubmitting}
                onClick={handleResend}
                type="button"
              >
                {cooldownRemaining > 0
                  ? `Resend code in ${cooldownRemaining}s`
                  : 'Resend code'}
              </button>
              <button
                className="mt-3 w-full text-sm text-slate-600 hover:text-slate-900"
                disabled={isSubmitting}
                onClick={() => {
                  setError('');
                  setCode('');
                  setStep('phone');
                }}
                type="button"
              >
                Use a different phone number
              </button>
            </form>
          </>
        )}
        <button
          className="mt-8 w-full text-sm text-slate-500 hover:text-slate-800"
          disabled={isSubmitting}
          onClick={onAbandon}
          type="button"
        >
          Back to sign in
        </button>
      </section>
    </main>
  );
};
